import {
  Component,
  Inject,
  inject,
  model,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { AbbButtonComponent } from '@components/abb-button/abb-button.component';
import { AbbModalComponent } from '@components/abb-modal/abb-modal.component';
import { MODAL_DATA, ModalData } from '@components/abb-modal/modal';
import {
  createDashboardMutation,
  duplicateDashboardMutation,
  updateDashboardMutation,
} from '@graphql/dashboard';
import { TranslateService } from '@ngx-translate/core';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { ToastService } from '@services/ui/toast.service';
import { t } from '@utils/strings';
import { Apollo } from 'apollo-angular';
import {
  CreateDashboardMutation,
  CreateDashboardMutationVariables,
  DuplicateDashboardMutation,
  DuplicateDashboardMutationVariables,
  GetDashboardsQuery,
  UpdateDashboardMutation,
  UpdateDashboardMutationVariables,
} from 'gql/graphql';
import { first } from 'rxjs';

export type DashboardModalData = {
  onSaved: (res: CreateDashboardMutation['createDashboard']) => void;
} & (
  | { mode: 'new'; dashboard?: undefined }
  | {
      mode: 'edit' | 'copy';
      dashboard: GetDashboardsQuery['getDashboards'][number];
    }
);

@Component({
  selector: 'abb-dashboard-modal',
  standalone: true,
  imports: [
    AbbModalComponent,
    TPipe,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    AbbButtonComponent,
  ],
  template: `
    <abb-modal
      [title]="
        (data.mode === 'new' ? 'Create Dashboard' : data.mode === 'edit' ? 'Edit Dashboard' : 'Duplicate Dashboard') | t
      "
      [closable]="!loading()"
    >
      <form
        class="flex flex-col flex-1 p-4 w-[90vw] sm:w-[85vw] md:w-[70vw] lg:w-auto"
        (ngSubmit)="save()"
      >
        <mat-form-field>
          <mat-label>{{ 'Name' | t }}</mat-label>
          <input
            matInput
            name="dashboardName"
            required
            [(ngModel)]="dashboardName"
          />
          @if (data.mode === 'new') {

          <mat-hint class="text-abb-font/65">{{
            'The name of your new dashboard, this will also serve as the dashboard title'
              | t
          }}</mat-hint>
          }
        </mat-form-field>
        <div class="flex justify-end items-center gap-x-4 mt-8 sm:mt-4">
          <abb-button
            [disabled]="loading()"
            variant="clean"
            type="button"
            (onclick)="onCancel()"
            >{{ 'cancel' | t }}</abb-button
          >
          <abb-button
            [disabled]="loading()"
            [loading]="loading()"
            type="submit"
            >{{ (data.mode === 'new' ? 'Create' : 'Save') | t }}</abb-button
          >
        </div>
      </form>
    </abb-modal>
  `,
  styles: ``,
  providers: [
    {
      provide: TRANSLATION_PREFIX,
      useValue: 'dashboards.modal',
    },
  ],
  host: { class: 'flex-1' },
})
export class DashboardModalComponent implements ModalData<DashboardModalData> {
  modalRef = viewChild(AbbModalComponent);
  loading = signal(false);
  dashboardName = model('');
  private prefix = inject(TRANSLATION_PREFIX);

  constructor(
    @Inject(MODAL_DATA) public data: DashboardModalData,
    private readonly apollo: Apollo,
    private readonly toast: ToastService,
    private readonly translate: TranslateService
  ) {
    if (data.dashboard) {
      this.dashboardName.set(data.dashboard.name);
    }
  }

  save() {
    this.loading.set(true);
    if (this.data.mode === 'new') {
      this.apollo
        .mutate<CreateDashboardMutation, CreateDashboardMutationVariables>({
          mutation: createDashboardMutation,
          variables: {
            name: this.dashboardName(),
          },
        })
        .subscribe({
          next: ({ data }) => {
            this.loading.set(false);
            this.modalRef()?.handleClose();
            this.toast.notifySuccess(
              t(this.prefix, `Dashboard created successfully`, this.translate)
            );
            this.apollo.client.refetchQueries({
              include: ['GetDashboards'],
            });
            if (data) {
              this.data.onSaved(data.createDashboard);
            }
          },
          error: () => {
            this.loading.set(false);
            this.toast.notifyError(
              t(
                this.prefix,
                `An error occurred while creating the dashboard`,
                this.translate
              )
            );
          },
        });
    } else if (this.data.mode === 'edit') {
      this.apollo
        .mutate<UpdateDashboardMutation, UpdateDashboardMutationVariables>({
          mutation: updateDashboardMutation,
          variables: {
            id: this.data.dashboard.id,
            name: this.dashboardName(),
          },
        })
        .subscribe({
          next: ({ data }) => {
            this.loading.set(false);
            this.modalRef()?.handleClose();
            this.toast.notifySuccess(
              t(this.prefix, `Dashboard updated successfully`, this.translate)
            );
            this.apollo.client.refetchQueries({
              include: ['GetDashboards'],
            });
            if (data) {
              this.data.onSaved(data.updateDashboard);
            }
          },
          error: () => {
            this.loading.set(false);
            this.toast.notifyError(
              t(
                this.prefix,
                `An error occurred while updating the dashboard`,
                this.translate
              )
            );
          },
        });
    } else if (this.data.mode === 'copy') {
      this.apollo.mutate<DuplicateDashboardMutation, DuplicateDashboardMutationVariables>({
        mutation: duplicateDashboardMutation,
        variables: {
          id: this.data.dashboard.id,
          name: this.dashboardName(),
        },
      }).subscribe({
        next: ({ data }) => {
          this.loading.set(false);
          this.modalRef()?.handleClose();
          this.toast.notifySuccess(
            t(this.prefix, `Dashboard duplicated successfully`, this.translate)
          );
          this.apollo.client.refetchQueries({
            include: ['GetDashboards'],
          });
          
        },
        error: () => {
          this.loading.set(false);
          this.toast.notifyError(
            t(
              this.prefix,
              `An error occurred while duplicating the dashboard`,
              this.translate
            )
          );
        },
      });
    }
  }

  onCancel() {
    this.modalRef()?.handleClose();
  }
}
